import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { setDemoExpirationModalDisplayed as setWelcomeMessageDisplayed2Call } from '../../lib/store/app';
import {
  getAccountExpirationDate,
  getCurrentAccount,
  hasAccountOnlyDemoPlan,
  hasAccountPaidAndDemoPlan,
} from '../../lib/store/crm/helpers';

import ExperienceIllustrationEffects from '../../assets/bigImages/experience_illustration_effects.png';
import { useIsMobile } from '../../hooks';
import { useTimeRemaining } from '../../hooks/useTimeRemaining';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';
import { BaseModal } from '../index';

import styles from './DemoAccountExpirationPopup.module.scss';

const DemoAccountExpirationPopup = (props: ReduxProps): JSX.Element | null => {
  const {
    currentAccount,
    expirationDate: endDate,
    isUseDemoAccount,
    isDemoExpirationModalDisplayed,
    setDemoExpirationModalDisplayed,
    hasOnlyDemoPlan,
    hasPaidAndDemoPlan,
  } = props;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const { hasNoRemainingTime } = useTimeRemaining(endDate);
  const [isDisplayModal, setIsDisplayModal] = useState(false);

  const isExpired = hasNoRemainingTime;

  useEffect(() => {
    if (isExpired) {
      setIsDisplayModal(true);
    }
  }, [isExpired]);

  const getTitle = (): string => {
    if (hasOnlyDemoPlan) {
      return lang.commonExpiredDemoAccountTitle();
    }

    if (hasPaidAndDemoPlan) {
      return lang.commonExpiredDemoAccountTitle();
    }

    return '';
  };

  const getDescription = (): string => {
    if (hasOnlyDemoPlan) {
      return lang.nowYouFamiliarPlatformProTrader();
    }

    if (hasPaidAndDemoPlan) {
      return lang.commonExpiredDemoAccountDesc();
    }

    return '';
  };

  const closeModal = (): void => {
    setIsDisplayModal(false);
    setDemoExpirationModalDisplayed(true);
  };

  if (isDemoExpirationModalDisplayed || !isDisplayModal || !isUseDemoAccount || !currentAccount) {
    return null;
  }

  return (
    <BaseModal
      customStyle={
        isMobile
          ? {
            paddingBottom: 0,
            margin: 0,
            width: window.innerWidth,
            position: 'fixed',
            alignSelf: 'flex-end',
          }
          : {}
      }
      className={styles.modal}
      isOpen={isDisplayModal}
    >
      <div className={styles.content}>
        <img src={ExperienceIllustrationEffects} className={styles.image} alt="" />
        <h6 className={styles.title}>{getTitle()}</h6>
        <span
          className={classNames(styles.descriptipon, {
            [styles.descriptiponExtended]: hasOnlyDemoPlan,
          })}
        >
          {getDescription()}
        </span>
      </div>
      {hasOnlyDemoPlan ? (
        <>
          <Button
            className={styles.productButton}
            variant={ButtonTypesEnum.Primary}
            onClick={() => {
              navigate(UI_ROUTES.products);
            }}
          >
            {lang.commonSelectPlan()}
          </Button>
          <Button
            className={styles.tryDemoButton}
            variant={ButtonTypesEnum.Tertiary}
            onClick={closeModal}
          >
            {isMobile ? lang.commonCancelButton() : lang.commonNotNow()}
          </Button>
        </>
      ) : (
        <Button
          className={styles.tryDemoButton}
          variant={ButtonTypesEnum.Primary}
          onClick={closeModal}
        >
          {lang.commonOkay()}
        </Button>
      )}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentAccount: getCurrentAccount(state),
  expirationDate: getAccountExpirationDate(state),
  isUseDemoAccount: state.crm.isUseDemoAccount,
  isDemoExpirationModalDisplayed: state.app.isDemoExpirationModalDisplayed,
  hasOnlyDemoPlan: hasAccountOnlyDemoPlan(state.crm),
  hasPaidAndDemoPlan: hasAccountPaidAndDemoPlan(state.crm),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  setDemoExpirationModalDisplayed: (value: boolean) =>
    dispatch(setWelcomeMessageDisplayed2Call(value)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DemoAccountExpirationPopup);
